import { F } from '@/utils/intl';
import { Alert } from 'antd';
import React, { useMemo } from 'react';
import { useLastGoodRender } from './lastGoodRender';
import { renderTwigPreview } from './twigRenderer';
import { blankEmptyImageSources } from './visual/canvasStyles';

import styles from './index.less';

interface PreviewPaneProps {
  html: string;
  previewContext?: Record<string, string>;
  translations?: Record<string, string>;
  /** Fragment key → Twig source for `{% include %}` / `{% extends %}`. */
  fragments?: Record<string, string>;
  /** Wraps the body in the layout exactly like the backend renderer. */
  layoutKey?: string | null;
  subjectText?: string;
  pretextText?: string;
  /** Extra sources whose variables prefill as raw placeholders (fragment editor). */
  prefillSources?: string[];
}

/**
 * Sandboxed, script-free rendering of the body HTML through the twig.js
 * preview renderer: variables, conditionals, the partner `|trans` filter and
 * DB fragments (layout via `layoutKey`, partials, blocks) are evaluated.
 * Sources the client cannot render (mid-edit syntax errors, missing
 * fragments) fall back to the naive substitution with a warning strip
 * explaining why — authoritative rendering always happens server-side.
 */
const PreviewPane: React.FC<PreviewPaneProps> = ({
  html,
  previewContext,
  translations,
  fragments,
  layoutKey,
  subjectText,
  pretextText,
  prefillSources,
}) => {
  const fresh = useMemo(
    () =>
      renderTwigPreview(html || '', {
        context: previewContext,
        translations,
        fragments,
        layoutKey,
        subjectText,
        pretextText,
        prefillSources,
      }),
    [
      html,
      previewContext,
      translations,
      fragments,
      layoutKey,
      subjectText,
      pretextText,
      prefillSources,
    ],
  );

  /**
   * A source that does not parse leaves the previous preview standing rather than
   * replacing it with the unrendered fallback — typing `{{` to open the variable list
   * would otherwise blank the pane mid-expression. See `lastGoodRender.ts`.
   */
  const { html: rawHtml, error, stale } = useLastGoodRender(fresh);

  /** Cheap content hash — identity is all the key needs, not reversibility. */
  /**
   * `src=""` resolves to this page's URL, so an image with nothing in it makes the
   * iframe download the whole backoffice as an image — see `blankEmptyImageSources`.
   */
  const srcDoc = useMemo(() => blankEmptyImageSources(rawHtml), [rawHtml]);

  const srcDocKey = useMemo(() => {
    let hash = 0;
    for (let i = 0; i < srcDoc.length; i += 1) {
      hash = (hash * 31 + srcDoc.charCodeAt(i)) | 0;
    }
    return `${srcDoc.length}:${hash}`;
  }, [srcDoc]);

  return (
    <div className={styles.previewPaneWrap}>
      {error && (
        <Alert
          className={styles.previewFallbackAlert}
          // `info` while the pane is merely held: an unfinished expression is a normal
          // moment in typing one, and a warning every time the variable list opens is
          // the interruption the held preview exists to remove.
          type={stale ? 'info' : 'warning'}
          showIcon
          message={
            stale ? (
              <F
                id="pages.emailCenter.preview.held"
                defaultMessage="Showing the last version that rendered — the code is unfinished: {error}"
                values={{ error }}
              />
            ) : (
              <F
                id="pages.emailCenter.preview.fallback"
                defaultMessage="Approximate preview — Twig rendering failed: {error}"
                values={{ error }}
              />
            )
          }
        />
      )}
      {/**
        * Keyed on the content, so React *remounts* the iframe when it changes.
        *
        * Updating `srcdoc` in place does not reliably re-navigate a sandboxed
        * frame: observed here with the attribute holding the correct 4 KB
        * document while the frame still displayed the placeholder from the very
        * first render, before the record had loaded into the form. Every later
        * edit was invisible, which is why the fragment preview looked frozen or
        * blank depending on what the first render happened to produce.
        *
        * Hashed rather than keyed on the string itself: the key is compared on
        * every render and these documents run to kilobytes.
        */}
      <iframe
        key={srcDocKey}
        className={styles.previewFrame}
        title="email-preview"
        sandbox=""
        srcDoc={srcDoc}
      />
    </div>
  );
};

export default React.memo(PreviewPane);
